import React, { useState, useEffect } from 'react';
import { UserProtocol } from '../../../types/protocols';
import { getUserProtocols } from '../../../utils/protocolApiUtils';
import './ProtocolSelectionDialog.scss';

interface ProtocolSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onProtocolSelect: (protocol: UserProtocol) => void;
  isLoading?: boolean;
}

const ProtocolSelectionDialog: React.FC<ProtocolSelectionDialogProps> = ({
  isOpen,
  onClose,
  onProtocolSelect,
  isLoading = false
}) => {
  const [protocols, setProtocols] = useState<UserProtocol[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProtocol, setSelectedProtocol] = useState<UserProtocol | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchActiveProtocols();
    }
  }, [isOpen]);

  const fetchActiveProtocols = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Получаем только активные протоколы
      const response = await getUserProtocols({ 
        status: 'active'
      });
      
      const activeProtocols = Array.isArray(response) 
        ? response.filter(p => p.is_active)
        : response.protocols?.filter(p => p.is_active) || [];
      
      setProtocols(activeProtocols);
    } catch (err) {
      console.error('Ошибка загрузки протоколов:', err);
      setError('Не удалось загрузить протоколы');
    } finally {
      setLoading(false);
    }
  };

  const handleProtocolClick = (protocol: UserProtocol) => {
    setSelectedProtocol(protocol);
  };

  const handleConfirm = () => {
    if (selectedProtocol) {
      onProtocolSelect(selectedProtocol);
      onClose();
      setSelectedProtocol(null);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedProtocol(null);
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="protocol-selection-overlay" onClick={handleClose}>
      <div className="protocol-selection-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="protocol-selection-header">
          <h2>Выберите протокол</h2>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="protocol-selection-content">
          {loading && (
            <div className="loading-state">
              <span className="loader-dots">Загрузка протоколов...</span>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p className="error-message">{error}</p>
              <button onClick={fetchActiveProtocols} className="retry-button">
                Попробовать снова
              </button>
            </div>
          )}

          {!loading && !error && protocols.length === 0 && (
            <div className="empty-state">
              <p>У вас нет активных протоколов</p>
              <p className="hint">Создайте протокол в разделе "Протоколы"</p>
            </div>
          )}

          {!loading && !error && protocols.length > 0 && (
            <div className="protocols-list">
              {protocols.map((protocol) => (
                <div
                  key={protocol.id}
                  className={`protocol-item ${selectedProtocol?.id === protocol.id ? 'selected' : ''}`}
                  onClick={() => handleProtocolClick(protocol)}
                >
                  <div className="protocol-info">
                    <h3 className="protocol-name">{protocol.name}</h3>
                    {protocol.description && (
                      <p className="protocol-description">{protocol.description}</p>
                    )}
                    <div className="protocol-meta">
                      {protocol.specialty && (
                        <span className="protocol-specialty">
                          Специальность: {protocol.specialty}
                        </span>
                      )}
                      <span className="protocol-version">
                        Последнее обновление: {protocol.updated_at ? new Date(protocol.updated_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </div>
                  <div className="protocol-status">
                    <span className="status-badge active">Активный</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="protocol-selection-footer">
          <button 
            className="button-secondary" 
            onClick={handleClose}
            disabled={isLoading}
          >
            Отмена
          </button>
          <button 
            className="button-primary" 
            onClick={handleConfirm}
            disabled={!selectedProtocol || isLoading}
          >
            {isLoading ? (
              <span className="loader-dots">Формирование...</span>
            ) : (
              'Сформировать протокол'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProtocolSelectionDialog;
